"use client";

import { memo, useEffect, useState } from "react";

interface FormSaveIndicatorProps {
  isSaving: boolean;
  lastSaved: Date | null;
  /** Custom theme color (hex). Falls back to indigo-500 if not provided. */
  themeColor?: string;
  /** When true, surface the "Saved on this device" disclosure (anonymous users keep their draft locally). */
  isLocalOnly?: boolean;
  /** Called when the user clicks "Clear draft" — should remove the draft from storage. */
  onClearDraft?: () => void | Promise<void>;
}

function formatRelativeTime(date: Date): string {
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  const diffMin = Math.floor(diffSec / 60);

  if (diffSec < 10) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin} min ago`;

  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/**
 * Displays autosave status. Wraps text in a polite live region so screen readers announce
 * state changes. Offers a clear-draft action to every user once a draft exists. For anonymous
 * users (`isLocalOnly`), additionally discloses that the draft lives on the device — required
 * because public forms collect PII.
 * @param props.isSaving - Whether a save is in progress
 * @param props.lastSaved - Timestamp of the last successful save
 * @param props.themeColor - Optional hex color for the saved state
 * @param props.isLocalOnly - Show the on-device disclosure (anonymous users)
 * @param props.onClearDraft - Handler invoked when the user clicks "Clear draft"
 */
export const FormSaveIndicator = memo(function FormSaveIndicator({
  isSaving,
  lastSaved,
  themeColor,
  isLocalOnly = false,
  onClearDraft,
}: FormSaveIndicatorProps) {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!lastSaved || isSaving) return;
    const interval = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(interval);
  }, [lastSaved, isSaving]);

  if (!lastSaved && !isSaving) return null;

  const colorClass = isSaving ? "text-gray-500" : !themeColor ? "text-indigo-600" : "";
  const colorStyle = !isSaving && themeColor ? { color: themeColor } : undefined;
  const savedLabel = lastSaved
    ? isLocalOnly
      ? `Saved on this device ${formatRelativeTime(lastSaved)}`
      : `Draft saved ${formatRelativeTime(lastSaved)}`
    : "";

  return (
    <div className="flex items-center gap-3">
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={`flex items-center gap-1.5 text-sm font-medium ${colorClass}`}
        style={colorStyle}
      >
        {isSaving ? (
          <>
            <span
              aria-hidden="true"
              className="inline-block h-2 w-2 motion-safe:animate-pulse rounded-full bg-gray-500"
            />
            <span>Saving…</span>
          </>
        ) : lastSaved ? (
          <>
            <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>{savedLabel}</span>
          </>
        ) : null}
      </div>
      {lastSaved && onClearDraft ? (
        <button
          type="button"
          onClick={() => onClearDraft()}
          className="touch-manipulation rounded p-2 text-xs font-medium text-gray-600 underline-offset-2 [@media(hover:hover)]:hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
        >
          Clear draft
        </button>
      ) : null}
    </div>
  );
});
